/**
 * FAMILY CATEGORIES round (2026-08-26, Matt: "Objects need categories they can be
 * assigned and which link to view filters and the layer settings in CAD exports.
 * Follow common revit family categories, for example Plumbing Fixtures,
 * Furniture, Etc."): a second, coarser classification axis ABOVE `CategoryId`.
 *
 * `CategoryId` stays the structural/geometric axis (what schema, what generator,
 * what pick rank) — unchanged. FAMILY is the organizational axis a firm actually
 * filters/exports by, and it does NOT map 1:1 to CategoryId: `object` alone spans
 * Plumbing Fixtures, Appliances, and Furniture; `fixture` spans Electrical
 * Fixtures and Lighting Fixtures. Every other category is a fixed 1:1 family
 * (a Wall is always "Walls" — Revit doesn't let you reassign a wall's family
 * category either), so only object/fixture carry an EDITABLE `familyCategory`
 * type param (object/fixture schema.ts) with a curated, category-scoped option
 * list; every other category's family is this file's static table, with no
 * per-type override — matching how Revit family categories actually behave
 * (fixed once a family is built from a category template).
 */
import type { CategoryId } from './types'
import { OBJECT_KINDS } from './categories/object/objectKinds'
import { FIXTURE_KINDS } from './categories/fixture/fixtureKinds'

/** the static family for every category that ISN'T further split by kind */
export const CATEGORY_FAMILY: Record<CategoryId, string> = {
  wall: 'Walls',
  floor: 'Floors',
  roof: 'Roofs',
  ceiling: 'Ceilings',
  door: 'Doors',
  window: 'Windows',
  stair: 'Stairs',
  ramp: 'Ramps',
  railing: 'Railings',
  fence: 'Fences',
  paving: 'Paving',
  casework: 'Casework',
  column: 'Structural Columns',
  beam: 'Structural Framing',
  topo: 'Topography',
  footing: 'Structural Foundations',
  stem: 'Structural Foundations',
  // fallback ONLY — object/fixture always resolve a kind-based family below;
  // this only fires for a malformed/unrecognized objectKind or fixtureKind
  object: 'Specialty Equipment',
  fixture: 'Electrical Fixtures',
  // the SketchUp-style face-edge kit (docs/bim-plan/21) — Revit's own name for
  // free-form massing geometry
  body: 'Generic Models',
  pool: 'Pools',
  // VINES: Revit's Planting
  vine: 'Planting',
  downspout: 'Roof Drainage',
  // WALL NICHE: Revit has no niche category; it is a wall-hosted specialty item
  niche: 'Specialty Equipment',
  // AWNINGS: Revit files canopies and awnings under Roofs; ours is its own category
  awning: 'Canopies & Awnings',
  // CUPOLAS: Revit has none; a roof accessory family of its own here
  cupola: 'Cupolas & Lanterns',
}

/** the curated family CHOICES for object/fixture's own editable `familyCategory`
 *  type param (schema.ts) — scoped to what's plausible for the category, not a
 *  free-for-all across every family in the model. '' = Auto (derive from kind). */
// FIT-OUT round (2026-09-30): 'Miscellaneous' is the Fit-Out panel's Misc bucket
// VEHICLES round (2026-10-01): 'Entourage' (Revit's category for cars, people, plants)
// PLANTING (2026-10-04): 'Planting' — Revit's own category for trees, shrubs and grasses
// SITE FURNISHINGS (2026-10-06): yard pieces — trampolines first
// SITE UTILITIES (2026-10-07): the gas meter set and the water meter box
export const OBJECT_FAMILY_CHOICES = ['', 'Plumbing Fixtures', 'Appliances', 'Furniture', 'Specialty Equipment', 'Entourage', 'Planting', 'Site Furnishings', 'Site Utilities', 'Outdoor Cooking', 'Miscellaneous'] as const
// FIT-OUT CATALOG (2026-09-30): Bath Accessories — the new wall-mounted family
// CEILING DEVICES (bim-plan/28 round 3): Fire Alarm Devices, Air Terminals (Revit's names)
export const FIXTURE_FAMILY_CHOICES = ['', 'Electrical Fixtures', 'Lighting Fixtures', 'Bath Accessories', 'Fire Alarm Devices', 'Air Terminals'] as const

const OBJECT_KIND_FAMILY: Record<string, string> = Object.fromEntries(
  OBJECT_KINDS.map((k) => [k.value, k.family]),
)
const FIXTURE_KIND_FAMILY: Record<string, string> = Object.fromEntries(
  FIXTURE_KINDS.map((k) => [k.value, k.family]),
)

/**
 * The resolved family for an element/type: an explicit `familyCategory` type
 * param wins (the user's own reassignment); object/fixture otherwise derive
 * from their `objectKind`/`fixtureKind` type param; every other category reads
 * the static table. `typeParams` accepts either a resolved element's
 * `typeParams` or a bare type record's own `params` — same shape either way.
 */
export function familyCategoryOf(
  category: CategoryId,
  typeParams: Record<string, unknown> | undefined,
): string {
  const explicit = typeParams?.familyCategory
  if (typeof explicit === 'string' && explicit) return explicit
  if (category === 'object') {
    const kind = String(typeParams?.objectKind ?? '')
    return OBJECT_KIND_FAMILY[kind] ?? CATEGORY_FAMILY.object
  }
  if (category === 'fixture') {
    const kind = String(typeParams?.fixtureKind ?? '')
    return FIXTURE_KIND_FAMILY[kind] ?? CATEGORY_FAMILY.fixture
  }
  return CATEGORY_FAMILY[category] ?? category
}

/**
 * Every family label that could ever appear in this model — the static table's
 * values plus every object/fixture kind's own family — for the view-filter UI
 * to enumerate. Sorted, deduped.
 */
export function listFamilies(): string[] {
  const set = new Set<string>(Object.values(CATEGORY_FAMILY))
  for (const f of Object.values(OBJECT_KIND_FAMILY)) set.add(f)
  for (const f of Object.values(FIXTURE_KIND_FAMILY)) set.add(f)
  return [...set].sort()
}

/**
 * VISIBILITY CONSOLIDATION round (2026-08-26, Matt: "Break objects into their
 * constituent families" — folding the Categories and Families view-filter
 * lists into one, since a category with families and its families were two
 * overlapping ways to hide the same content): just the KIND-derived families
 * (Plumbing Fixtures, Appliances, Furniture, Electrical Fixtures, Lighting
 * Fixtures) — what a merged category/family list shows IN PLACE OF the raw
 * 'object'/'fixture' rows, never alongside them. Sorted, deduped.
 */
export function objectFixtureFamilies(): string[] {
  const set = new Set<string>()
  for (const f of Object.values(OBJECT_KIND_FAMILY)) set.add(f)
  for (const f of Object.values(FIXTURE_KIND_FAMILY)) set.add(f)
  return [...set].sort()
}

/**
 * Ribbon buttons that narrow the object picker more tightly than a whole family
 * (2026-10-07, team report: Pool > Equipment Pad opened the ENTIRE Objects library —
 * the pad's family, Specialty Equipment, also holds all the HVAC). Display-only
 * filter tokens: they never become a type's family, a view filter or a CAD layer.
 */
export const TOOL_SUBFAMILIES: Record<string, { category: CategoryId; objectKinds: string[] }> = {
  'Pool Equipment': { category: 'object' as CategoryId, objectKinds: ['poolEquipment'] },
}

/** does this type belong under a tool's family filter (a real family or a TOOL_SUBFAMILIES token)? */
export function inToolFamily(
  category: CategoryId,
  typeParams: Record<string, unknown> | undefined,
  family: string,
): boolean {
  const sub = TOOL_SUBFAMILIES[family]
  if (sub) return category === sub.category && sub.objectKinds.includes(String(typeParams?.objectKind ?? ''))
  return familyCategoryOf(category, typeParams) === family
}
